import { useState, useEffect, useCallback } from "react";
import { format, startOfMonth } from "date-fns";
import { DollarSign, Users, BarChart2, Table2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { DateRangePicker } from "@/components/DateRangePicker";
import { partnerApi } from "@/lib/api";
import { formatMoney, currencySymbol } from "@/lib/money";
import { toast } from "sonner";

export default function PartnerDashboard({ fetchDashboard = partnerApi.getDashboard, fetchDates = partnerApi.getAvailableDates, preview = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState("chart");
  const [availableDates, setAvailableDates] = useState([]);
  const user = JSON.parse(localStorage.getItem("auth_user") || "{}");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
      if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
      const res = await fetchDashboard(params);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAvailableDates = useCallback(async () => {
    try {
      const res = await fetchDates();
      setAvailableDates(res.data);
    } catch (_) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAvailableDates(); }, [fetchAvailableDates]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const summary = data?.summary || {};
  const daily = data?.daily || [];
  const tableData = data?.table || [];
  const partnerInfo = data?.partner || {};
  const curr = partnerInfo.display_currency || "USD";
  const sym = currencySymbol(curr);
  const isProjected = partnerInfo.is_projected;

  const chartData = daily.map((d) => ({
    date: d.ymd.slice(5),
    Earnings: d.earnings,
    "Active Users": d.peers,
  }));

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="partner-dashboard">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Welcome, {preview ? partnerInfo.partner_name : (user.partner_name || "Partner")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Attribution keys: <span className="font-mono text-slate-700">{(partnerInfo.campaign_ids || user.campaign_ids || []).join(", ")}</span>
          </p>
        </div>
        <DateRangePicker startDate={startDate} endDate={endDate} onStartChange={setStartDate} onEndChange={setEndDate} availableDates={availableDates} isAdmin={false} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="kpi-card animate-fade-in" data-testid="partner-kpi-earnings">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <DollarSign size={20} className="text-blue-600" strokeWidth={1.5} />
            </div>
            <span className="kpi-label">Your Earnings</span>
          </div>
          <div className="kpi-value" data-testid="partner-earnings-value">{formatMoney(summary.total_earnings, curr)}</div>
          {isProjected && (
            <p className="text-[11px] text-amber-600 mt-1" data-testid="partner-projected-note">Shown in USD — final ₹ amount is set on your invoice at the payment-date rate.</p>
          )}
        </div>
        <div className="kpi-card animate-fade-in animate-fade-in-delay-1" data-testid="partner-kpi-peers">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users size={20} className="text-blue-600" strokeWidth={1.5} />
            </div>
            <span className="kpi-label">Avg Active Users</span>
          </div>
          <div className="kpi-value">{summary.avg_peers?.toLocaleString() || "0"}</div>
        </div>
        <div className="kpi-card animate-fade-in animate-fade-in-delay-2" data-testid="partner-kpi-days">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <BarChart2 size={20} className="text-emerald-600" strokeWidth={1.5} />
            </div>
            <span className="kpi-label">Days Active</span>
          </div>
          <div className="kpi-value">{summary.total_days || 0}</div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setViewMode("chart")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === "chart" ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid="partner-view-chart-btn"
        >
          <BarChart2 size={16} /> Chart
        </button>
        <button
          onClick={() => setViewMode("table")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === "table" ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid="partner-view-table-btn"
        >
          <Table2 size={16} /> Table
        </button>
      </div>

      {/* Chart View */}
      {viewMode === "chart" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="chart-container" data-testid="partner-earnings-chart">
            <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Your Earnings</h3>
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} tickFormatter={(v) => `${sym}${v}`} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
                    formatter={(value) => [`${sym}${value.toFixed(2)}`, undefined]}
                  />
                  <Area type="monotone" dataKey="Earnings" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="chart-container" data-testid="partner-users-chart">
            <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Daily Active Users</h3>
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} tickFormatter={(v) => v.toLocaleString()} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
                  <Bar dataKey="Active Users" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <div className="data-table-container" data-testid="partner-earnings-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Date</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Partner ID</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 text-right">Earnings</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 text-right">Active Users</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12 text-slate-400">No data available for your attribution keys.</TableCell>
                </TableRow>
              ) : (
                tableData.map((row, i) => (
                  <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <TableCell className="py-3 px-4 text-sm text-slate-700">{row.ymd}</TableCell>
                    <TableCell className="py-3 px-4 text-sm font-mono text-slate-600">{row.partner_id}</TableCell>
                    <TableCell className="py-3 px-4 text-sm text-right font-mono text-blue-700 font-medium">{row.earnings === null ? <span className="text-slate-400 italic font-sans text-xs" data-testid="partner-cell-unavailable">Unavailable</span> : formatMoney(row.earnings, curr)}</TableCell>
                    <TableCell className="py-3 px-4 text-sm text-right font-mono text-slate-700">{row.peers?.toLocaleString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
